import React, { useRef, useState, useEffect, ClipboardEvent } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../../../context/AuthContext";
import { verifyMfa } from "../../../api/auth/verify_mfa";
import FlashMessage from "../../../components/common/FlashMessage";
import { useFlashMessage } from "../../../hooks/useFlashMessage";
import logo from "../../../assets/Logo.png";
// eslint-disable-next-line @typescript-eslint/no-require-imports
require("./VerifyMfa.css");

// Mascara o e-mail: "usuario@email.com" → "u***o@email.com"
function maskEmail(email: string): string {
  const [local, domain] = email.split("@");
  if (!domain) return email;
  if (local.length <= 2) return `${local[0]}***@${domain}`;
  return `${local[0]}***${local[local.length - 1]}@${domain}`;
}

const CODE_LENGTH = 6;

const VerifyMfa: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { setAuthUser } = useAuth();
  const { message, type, showMessage, clearMessage } = useFlashMessage(6000);

  // O e-mail vem via state de navegação definido no Login
  const email: string = (location.state as any)?.email || "";

  const [digits, setDigits] = useState<string[]>(Array(CODE_LENGTH).fill(""));
  const [loading, setLoading] = useState(false);
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);

  useEffect(() => {
    if (!email) {
      // Nenhum email em state — redirecionar para login
      navigate("/login", { replace: true });
    }
    inputRefs.current[0]?.focus();
  }, [email, navigate]);

  const handleChange = (index: number, value: string) => {
    const char = value.replace(/\D/g, "").slice(-1);
    const next = [...digits];
    next[index] = char;
    setDigits(next);
    if (char && index < CODE_LENGTH - 1) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handleKeyDown = (
    index: number,
    e: React.KeyboardEvent<HTMLInputElement>
  ) => {
    if (e.key === "Backspace" && !digits[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  const handlePaste = (e: ClipboardEvent<HTMLInputElement>) => {
    e.preventDefault();
    const pasted = e.clipboardData
      .getData("text")
      .replace(/\D/g, "")
      .slice(0, CODE_LENGTH);
    const next = [...digits];
    for (let i = 0; i < pasted.length; i++) next[i] = pasted[i];
    setDigits(next);
    const focusIndex = Math.min(pasted.length, CODE_LENGTH - 1);
    inputRefs.current[focusIndex]?.focus();
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const code = digits.join("");
    if (code.length < CODE_LENGTH) {
      showMessage("Digite o código de 6 dígitos completo.", "warning");
      return;
    }
    setLoading(true);
    try {
      const result = await verifyMfa(email, code);
      setAuthUser(result);
      navigate("/searchscreen");
    } catch (err: any) {
      if (err.isExpired) {
        showMessage(
          "Código expirado. Faça login novamente.",
          "warning"
        );
        setTimeout(() => navigate("/login", { replace: true }), 2500);
      } else {
        showMessage("Código incorreto. Verifique e tente novamente.", "error");
        setDigits(Array(CODE_LENGTH).fill(""));
        inputRefs.current[0]?.focus();
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="mfa-container">
      {message && (
        <div
          style={{
            position: "fixed",
            top: "20px",
            left: "50%",
            transform: "translateX(-50%)",
            zIndex: 9999,
            minWidth: "400px",
          }}
        >
          <FlashMessage message={message} type={type} onClose={clearMessage} />
        </div>
      )}

      <div className="mfa-box">
        <div className="mfa-logo" onClick={() => navigate("/home")}>
          <img src={logo} alt="Logo" width={40} height={40} />
          <span>Insecta</span>
        </div>

        <h2 className="mfa-title">Verificação em dois fatores</h2>
        <p className="mfa-subtitle">
          Um código de 6 dígitos foi enviado para
          <br />
          <strong>{maskEmail(email)}</strong>
          <br />O código expira em 10 minutos.
        </p>

        <form onSubmit={handleSubmit} style={{ width: "100%" }}>
          <div className="mfa-code-inputs">
            {digits.map((d, i) => (
              <input
                key={i}
                ref={(el) => (inputRefs.current[i] = el)}
                type="text"
                inputMode="numeric"
                maxLength={1}
                value={d}
                onChange={(e) => handleChange(i, e.target.value)}
                onKeyDown={(e) => handleKeyDown(i, e)}
                onPaste={i === 0 ? handlePaste : undefined}
                autoComplete="off"
              />
            ))}
          </div>

          <div style={{ marginTop: "1.5rem" }}>
            <button type="submit" className="mfa-btn" disabled={loading}>
              {loading ? "Verificando..." : "Confirmar"}
            </button>
          </div>
        </form>

        <p className="mfa-back-link">
          Não recebeu o código?{" "}
          <a href="/login" onClick={(e) => { e.preventDefault(); navigate("/login"); }}>
            Voltar ao login
          </a>
        </p>
      </div>
    </div>
  );
};

export default VerifyMfa;
